import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Seo from "../components/Seo";
import "../components/booth/tokens.css";
import BoothMark from "../components/booth/BoothMark";
import BoothField from "../components/booth/BoothField";
import Footer from "../components/booth/Footer";
import "./JoinLeague.css";

const API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://thebooth.gg"
    : "http://localhost:8000";

function JoinLeague() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [leagueName, setLeagueName] = useState("");
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  async function loadJoinInfo() {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/join/${token}`);
      if (response.data.allClaimed) {
        setStatus("allClaimed");
        return;
      }
      setLeagueName(response.data.leagueName);
      setMembers(response.data.members);
      setSelectedMemberId(response.data.members[0]?.id || "");
      setStatus("ready");
    } catch (error) {
      setStatus("invalid");
    }
  }

  useEffect(() => {
    loadJoinInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setStatus("submitting");
    try {
      await axios.post(`${API_BASE_URL}/join/${token}/claim`, {
        memberId: selectedMemberId,
        email: email,
      });
      setStatus("success");
    } catch (error) {
      const code = error.response?.data?.error;
      setErrorMessage(
        code === "ALREADY_CLAIMED"
          ? "That team was just claimed — pick another."
          : "Something went wrong. Please try again."
      );
      await loadJoinInfo();
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return <p className="booth-join-status">Loading…</p>;

      case "invalid":
        return (
          <>
            <h1 className="booth-auth-title">This invite link isn't valid.</h1>
            <p className="booth-join-dek">Ask your league manager to resend it.</p>
          </>
        );

      case "allClaimed":
        return (
          <>
            <h1 className="booth-auth-title">Everyone's already signed up!</h1>
            <p className="booth-join-dek">
              Every team in this league already has an email on file.
            </p>
          </>
        );

      case "success":
        return (
          <>
            <h1 className="booth-auth-title">You're in!</h1>
            <p className="booth-join-dek">
              The weekly dispatch will start landing in your inbox.
            </p>
          </>
        );

      case "ready":
      case "submitting":
        return (
          <>
            <p className="booth-auth-eyebrow">Step inside</p>
            <h1 className="booth-auth-title">Join {leagueName}</h1>
            <form onSubmit={handleSubmit} className="booth-auth-form" noValidate>
              <BoothField
                id="join-team-select"
                label="Which team is yours?"
                name="team"
                type="select"
                value={selectedMemberId}
                onChange={(event) => setSelectedMemberId(event.target.value)}
                options={members.map((member) => ({ value: member.id, label: member.name }))}
              />
              <BoothField
                id="join-email"
                label="Your email"
                name="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                error={errorMessage}
              />
              <button
                type="submit"
                className="booth-btn booth-btn--primary booth-auth-submit"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Joining…" : "Join league"}
              </button>
            </form>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="booth booth-join-page">
      <Seo
        title="Join your league — The Booth"
        description="Claim your team to start getting the weekly dispatch."
        path={`/join/${token}`}
        noindex
      />
      <header className="booth-shell booth-join-masthead">
        <a
          href="/"
          className="booth-join-mark-link"
          onClick={(e) => {
            e.preventDefault();
            navigate("/");
          }}
        >
          <BoothMark />
        </a>
      </header>
      <main className="booth-shell booth-join-main">
        <div className="booth-auth-card">{renderContent()}</div>
      </main>
      <Footer />
    </div>
  );
}

export default JoinLeague;
